import React, { useEffect, useReducer, useRef } from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { LoaderDots } from '../comps/FetchingState';
import { useRiskIntel } from '../useRiskIntel';
import { useScrollOnReveal } from '../useScrollOnReveal';
import { initialWizardState, wizardReducer } from '../wizardReducer';
import { NarrativeBanner } from '../comps/NarrativeBanner';
import { RiskDescriptionCard } from '../comps/RiskDescriptionCard';
import { ResponsesPager } from '../comps/ResponsesPager';
import { EmptyStateCard } from '../comps/EmptyStateCard';
import { SuggestedScenarioCard } from '../comps/SuggestedScenarioCard';
import { ResultBanner } from '../comps/ResultBanner';

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 308px;
  background: ${({ theme }) => theme.colors.cultured};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
`;

// Shared outer border so a warning/invalid banner blends into the card below as one card.
const Merged = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  overflow: hidden;
`;

interface NarrativeStepProps {
  workspaceId?: string;
}

export const NarrativeStep: React.FC<NarrativeStepProps> = ({ workspaceId }) => {
  const { session, pending, startSession, refine, selectCycle } = useRiskIntel();
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);

  const cycles = session?.cycles ?? [];
  const suggestions = cycles.filter((cycle) => !!cycle.risk);
  const latest = cycles[cycles.length - 1];
  const isLatestInvalid = latest?.validation?.state === 'invalid';
  const hasSelected = !!session?.selectedCycleId;
  const selectedCycle = cycles.find((cycle) => cycle.id === session?.selectedCycleId);

  useEffect(() => {
    if (suggestions.length > 0) dispatch({ type: 'SET_PAGE', value: suggestions.length - 1 });
  }, [suggestions.length]);

  // On session present→null (reset), wipe the textarea; guard the transition so typing the first
  // description (session stays null) never clears mid-keystroke.
  const prevSessionRef = useRef(session);
  useEffect(() => {
    if (prevSessionRef.current && !session) dispatch({ type: 'SET_INPUT', value: '' });
    prevSessionRef.current = session;
  }, [session]);

  // Hydrate the textarea from the last submitted input on resume/reload — the cycle's userInput is
  // restored on the session but the reducer starts empty. One-shot, and only when empty, so it never
  // clobbers in-progress typing (fresh start: startSession sets it to the same value) or a start-over.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !session) return;
    hydratedRef.current = true;
    if (state.narrativeInput.trim().length > 0) return;
    const cycle = cycles.find((c) => c.id === session.selectedCycleId) ?? cycles[cycles.length - 1];
    if (cycle?.userInput) dispatch({ type: 'SET_INPUT', value: cycle.userInput });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const page = Math.min(state.suggestionPage, Math.max(0, suggestions.length - 1));
  const current = suggestions[page];
  const total = suggestions.length;
  const analysing = pending && !state.confirmingAccept && !hasSelected;

  // Scroll to the loader, then push again once the suggestion lands (taller than the loader);
  // suppressed once accepted so resuming past the narrative doesn't yank back up.
  const resultPhase = hasSelected ? false : analysing ? 'analysing' : latest ? `result-${latest.id}` : false;
  const resultRef = useScrollOnReveal(resultPhase);

  const handleAnalyse = async () => {
    if (state.narrativeInput.trim().length === 0 || pending || hasSelected) return;
    if (session) await refine(state.narrativeInput);
    else await startSession(state.narrativeInput, workspaceId);
  };

  // Capture the cycle when the dialog opens — a refetch/auto-advance can change `current` mid-dialog
  // and accept the wrong cycle.
  const confirmCycleIdRef = useRef<string | null>(null);

  const handleConfirmAccept = async () => {
    const cycleId = confirmCycleIdRef.current ?? current?.id;
    if (!cycleId) return;
    await selectCycle(cycleId);
    confirmCycleIdRef.current = null;
    dispatch({ type: 'CANCEL_CONFIRM' });
  };

  const renderBody = () => {
    if (hasSelected && selectedCycle?.risk) {
      return (
        <SuggestedScenarioCard
          risk={selectedCycle.risk}
          accepted
          confirming={false}
          pending={false}
          onAccept={() => undefined}
          onConfirm={() => undefined}
          onCancel={() => undefined}
          onTryAgain={() => undefined}
        />
      );
    }

    if (analysing) {
      return (
        <Loading>
          <LoaderDots />
        </Loading>
      );
    }

    if (cycles.length === 0) return <EmptyStateCard />;

    if (isLatestInvalid && suggestions.length === 0) {
      return (
        <Merged>
          <ResultBanner
            variant="invalid"
            heading={latest?.validation?.warningMessage ?? 'Description is not a risk'}
            body={
              latest?.validation?.reason ??
              "We couldn't identify a risk in your description above, so we cannot generate a suggested scenario. Enter a valid risk description above containing a source, event, and consequence."
            }
            suggestion={latest?.validation?.suggestion}
            onUseSuggestion={
              latest?.validation?.suggestion
                ? () => dispatch({ type: 'SET_INPUT', value: latest!.validation!.suggestion! })
                : undefined
            }
          />
          <EmptyStateCard bare />
        </Merged>
      );
    }

    if (!current?.risk) return <EmptyStateCard />;

    const cardProps = {
      risk: current.risk,
      accepted: false,
      confirming: state.confirmingAccept,
      pending,
      onAccept: () => {
        confirmCycleIdRef.current = current?.id ?? null;
        dispatch({ type: 'START_CONFIRM' });
      },
      onConfirm: handleConfirmAccept,
      onCancel: () => dispatch({ type: 'CANCEL_CONFIRM' }),
      onTryAgain: handleAnalyse,
    };

    if (current.validation?.state === 'warning') {
      return (
        <Merged>
          <ResultBanner
            variant="warning"
            heading={current.validation?.warningMessage ?? 'Description missing some details'}
            body={
              current.validation?.reason ??
              "This description doesn't have a clear source, event, and consequence. We have generated a suggested scenario based on our best guess of what your description means."
            }
            suggestion={current.validation?.suggestion}
            onUseSuggestion={
              current.validation?.suggestion
                ? () => dispatch({ type: 'SET_INPUT', value: current.validation!.suggestion! })
                : undefined
            }
          />
          <SuggestedScenarioCard {...cardProps} bare />
        </Merged>
      );
    }

    return <SuggestedScenarioCard {...cardProps} />;
  };

  return (
    <div>
      <NarrativeBanner />
      <Spacer $px={24} />

      <Poppins px={16} weight={600} color="cflowerBlue" css="text-transform: uppercase;">
        Risk Description
      </Poppins>
      <Spacer $px={12} />
      <RiskDescriptionCard
        value={state.narrativeInput}
        onChange={(value) => dispatch({ type: 'SET_INPUT', value })}
        onAnalyse={handleAnalyse}
        analysing={analysing}
        disabled={hasSelected}
      />

      <div ref={resultRef}>
        <ResponsesPager
          current={total === 0 ? 0 : page + 1}
          total={total}
          onPrev={() => dispatch({ type: 'SET_PAGE', value: page - 1 })}
          onNext={() => dispatch({ type: 'SET_PAGE', value: page + 1 })}
        />

        {renderBody()}
      </div>
    </div>
  );
};
